import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import { useNavigate } from 'react-router-dom';

const UIUXDesign = () => {
  const navigate = useNavigate();

  const seoData = {
    title: 'UI/UX Design Services - User-Centered Design | OctalChip',
    description: 'User-centered design that combines aesthetics with functionality. We create intuitive interfaces and engaging user experiences that drive conversions.',
    keywords: 'UI design, UX design, user interface, user experience, wireframing, prototyping, design systems',
    canonical: 'https://www.octalchip.com/services/ui-ux-design',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "UI/UX Design Services",
      "description": "User-centered design that combines aesthetics with functionality",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip"
      }
    }
  };

  const features = [
    {
      title: 'User Research',
      description: 'Comprehensive user research to understand needs, behaviors, and pain points',
      icon: 'Users'
    },
    {
      title: 'Wireframing',
      description: 'Create detailed wireframes to visualize structure and user flows',
      icon: 'Layout'
    },
    {
      title: 'Prototyping',
      description: 'Interactive prototypes for testing and validation before development',
      icon: 'Zap'
    },
    {
      title: 'Design Systems',
      description: 'Comprehensive design systems for consistency and scalability',
      icon: 'Layers'
    },
    {
      title: 'Usability Testing',
      description: 'Test designs with real users to identify issues and improve UX',
      icon: 'CheckCircle'
    },
    {
      title: 'Visual Design',
      description: 'Beautiful, modern interfaces that align with your brand identity',
      icon: 'Palette'
    }
  ];

  const technologies = [
    { name: 'Figma', category: 'Design Tool', description: 'Collaborative interface design tool' },
    { name: 'Adobe XD', category: 'Design Tool', description: 'UX/UI design and prototyping platform' },
    { name: 'Sketch', category: 'Design Tool', description: 'Digital design toolkit for Mac' },
    { name: 'InVision', category: 'Prototyping', description: 'Digital product design platform' },
    { name: 'Principle', category: 'Animation', description: 'Interactive design and animation tool' },
    { name: 'Framer', category: 'Prototyping', description: 'Advanced prototyping and interaction design' },
    { name: 'Adobe Illustrator', category: 'Graphics', description: 'Vector graphics and illustration' },
    { name: 'Adobe Photoshop', category: 'Graphics', description: 'Image editing and manipulation' },
    { name: 'Miro', category: 'Collaboration', description: 'Collaborative whiteboard for ideation' }
  ];

  const useCases = [
    {
      title: 'Web Application Design',
      description: 'Intuitive web interfaces that enhance user engagement and conversions',
      icon: 'Globe'
    },
    {
      title: 'Mobile App Design',
      description: 'Native mobile experiences following iOS and Android design guidelines',
      icon: 'Smartphone'
    },
    {
      title: 'Dashboard Design',
      description: 'Data visualization dashboards with clear information hierarchy',
      icon: 'BarChart'
    },
    {
      title: 'E-commerce Design',
      description: 'Shopping experiences optimized for conversion and user satisfaction',
      icon: 'ShoppingCart'
    },
    {
      title: 'SaaS Platform Design',
      description: 'Complex SaaS interfaces with intuitive navigation and workflows',
      icon: 'Cloud'
    },
    {
      title: 'Brand Identity',
      description: 'Complete brand identity systems including logos, colors, and typography',
      icon: 'Award'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Discovery & Research',
      description: 'Understand business goals, target users, and competitive landscape',
      icon: 'Search'
    },
    {
      step: '02',
      title: 'User Personas & Journey',
      description: 'Create user personas and map user journeys to identify pain points',
      icon: 'Users'
    },
    {
      step: '03',
      title: 'Information Architecture',
      description: 'Design site structure, navigation, and content organization',
      icon: 'Layers'
    },
    {
      step: '04',
      title: 'Wireframing & Prototyping',
      description: 'Create wireframes and interactive prototypes for testing',
      icon: 'Layout'
    },
    {
      step: '05',
      title: 'Visual Design',
      description: 'Design beautiful interfaces with colors, typography, and imagery',
      icon: 'Palette'
    },
    {
      step: '06',
      title: 'Testing & Iteration',
      description: 'Test with users, gather feedback, and iterate for optimal UX',
      icon: 'CheckCircle'
    }
  ];

  const benefits = [
    'Increase user engagement and satisfaction',
    'Improve conversion rates and revenue',
    'Reduce development time and costs',
    'Enhance brand perception and trust',
    'Decrease support requests and errors',
    'Create scalable design systems',
    'Improve accessibility and inclusivity',
    'Stand out from competitors'
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Enhanced Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-[#00d690]/10 via-white to-[#00d690]/5 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #00d690 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-2xl mb-8 shadow-lg transform hover:scale-110 transition-transform duration-300">
              <Icon name="LayoutTemplate" size={48} className="text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-800 mb-6 leading-tight">
              UI/UX Design
              <span className="block text-[#00d690] mt-2">Services</span>
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mx-auto mb-8"></div>
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
              Create exceptional user experiences that delight users and drive business results. 
              We combine user research, thoughtful design, and testing to build interfaces that work beautifully.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                variant="primary" 
                onClick={() => navigate('/services#share-your-requirements')}
                className="px-10 py-4 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                Start Your Project
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/services#services')}
                className="px-10 py-4 text-lg border-2"
              >
                View All Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">30+</div>
              <div className="text-gray-600 font-medium">Design Projects</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">1-4</div>
              <div className="text-gray-600 font-medium">Weeks Timeline</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">40%</div>
              <div className="text-gray-600 font-medium">Conversion Increase</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">100%</div>
              <div className="text-gray-600 font-medium">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Design Services</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive UI/UX design services for exceptional user experiences
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-xl flex items-center justify-center mb-6 group-hover:from-[#00d690] group-hover:to-[#00b876] transition-all duration-300">
                  <Icon name={feature.icon} size={28} className="text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Design Tools</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Industry-leading design and prototyping tools we use
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {technologies.map((tech, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-gray-800 group-hover:text-[#00d690] transition-colors">
                    {tech.name}
                  </span>
                  <span className="text-xs font-medium text-[#00d690] bg-[#00d690]/10 px-2 py-1 rounded">
                    {tech.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {tech.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-gradient-to-br from-[#00d690]/5 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">What We Design</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive design solutions for every digital product
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group border-l-4 border-[#00d690]"
              >
                <div className="w-14 h-14 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#00d690] transition-all duration-300">
                  <Icon name={useCase.icon} size={28} className="text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">
                  {useCase.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Design Process</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A user-centered design methodology for exceptional results
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="relative">
              <div className="hidden md:block absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00d690] to-[#00b876]"></div>
              
              <div className="space-y-12">
                {processSteps.map((step, index) => (
                  <div key={index} className="relative flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10">
                      {step.step}
                    </div>
                    
                    <div className="flex-1 bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-4">
                          <Icon name={step.icon} size={24} className="text-[#00d690]" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed text-lg">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Benefits of Great Design</h2>
              <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4 bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:bg-white/10 transition-all">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#00d690] rounded-full flex items-center justify-center">
                    <Icon name="Check" size={20} className="text-white" />
                  </div>
                  <p className="text-lg text-gray-200">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Ready to Design Your User Experience?
            </h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Let's create beautiful, user-centered designs that drive engagement and conversions. 
              Get a free consultation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/services#share-your-requirements')}
                className="bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                Start Your Project
              </button>
              <button
                onClick={() => navigate('/services#services')}
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-4 px-8 rounded-lg transition-all duration-300"
              >
                View All Services
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UIUXDesign;
